<template>
  <div class="branch-dashboard">
    <v-container fluid class="pa-6">
      <!-- Header Section -->
      <div class="dashboard-header mb-8">
        <div class="header-content">
          <div class="header-title-section">
            <h1 class="dashboard-title">🌿 สถิตินักเรียน/นักศึกษาตามสาขาวิชา</h1>
            <p class="dashboard-subtitle">
              สำนักงานคณะกรรมการการอาชีพวศึกษา (สอศ.)
            </p>
          </div>
          <div class="header-actions">
            <v-btn
              color="success"
              variant="tonal"
              prepend-icon="mdi-file-excel"
              @click="exportToExcel"
              :loading="exporting"
              size="large"
            >
              ส่งออก Excel
            </v-btn>
            <v-btn
              color="primary"
              variant="tonal"
              prepend-icon="mdi-refresh"
              @click="fetchData"
              :loading="isLoading"
              size="large"
            >
              รีเฟรช
            </v-btn>
          </div>
        </div>
      </div>

      <!-- Filter Section -->
      <v-card class="filter-card mb-8" elevation="2">
        <v-card-text class="pa-6">
          <div class="filter-grid">
            <!-- Branch Filter -->
            <div class="filter-item">
              <label class="filter-label">สาขาวิชา</label>
              <v-autocomplete
                :model-value="filters.branch_name"
                @change="updateBranchFilter"
                :items="uniqueBranches"
                label="เลือกสาขา"
                outlined
                dense
                clearable
                class="filter-input"
              />
            </div>

            <!-- Year Filter -->
            <div class="filter-item">
              <label class="filter-label">ปีการศึกษา</label>
              <v-autocomplete
                :model-value="filters.academic_year"
                @change:model-value="updateYearFilter"
                :items="uniqueYears"
                label="เลือกปี"
                outlined
                dense
                clearable
                class="filter-input"
              />
            </div>

            <!-- Action Buttons -->
            <div class="filter-actions">
              <v-btn
                class="btn-reset"
                outlined
                color="secondary"
                @click="resetFilters"
                size="small"
              >
                🔄 ล้างตัวกรอง
              </v-btn>
            </div>
          </div>
        </v-card-text>
      </v-card>

      <!-- Statistics Cards -->
      <div class="stats-row mb-8">
        <v-card class="stat-card stat-card-total" elevation="2">
          <v-card-text class="pa-6">
            <div class="stat-icon">👥</div>
            <div class="stat-info">
              <div class="stat-label">รวมนักเรียน/นักศึกษาทั้งหมด</div>
              <div class="stat-value">{{ formatNumber(totalStudents) }}</div>
            </div>
          </v-card-text>
        </v-card>

        <v-card class="stat-card stat-card-branch" elevation="2">
          <v-card-text class="pa-6">
            <div class="stat-icon">🏫</div>
            <div class="stat-info">
              <div class="stat-label">จำนวนสาขาวิชา</div>
              <div class="stat-value">{{ uniqueBranches.length }}</div>
            </div>
          </v-card-text>
        </v-card>

        <v-card class="stat-card stat-card-college" elevation="2">
          <v-card-text class="pa-6">
            <div class="stat-icon">🎓</div>
            <div class="stat-info">
              <div class="stat-label">จำนวนวิทยาลัย</div>
              <div class="stat-value">{{ totalColleges }}</div>
            </div>
          </v-card-text>
        </v-card>

        <v-card class="stat-card stat-card-province" elevation="2">
          <v-card-text class="pa-6">
            <div class="stat-icon">📍</div>
            <div class="stat-info">
              <div class="stat-label">จำนวนจังหวัด</div>
              <div class="stat-value">{{ totalProvinces }}</div>
            </div>
          </v-card-text>
        </v-card>
      </div>

      <!-- Charts Grid - Row 1 -->
      <div class="charts-grid mb-8">
        <!-- Bar Chart: Top Branches by Student Count -->
        <v-card class="chart-card" elevation="2">
          <v-card-text class="pa-6">
            <div class="chart-header">
              <h3 class="chart-title">📊 สาขาวิชา Top 15 (จำนวนนักเรียน)</h3>
            </div>
            <div v-if="!isLoading" class="chart-container">
              <div v-if="hasTopBranchesData" class="chart-wrapper">
                <apexchart
                  v-if="chartTopBranchesSeries.length > 0"
                  type="bar"
                  :options="chartTopBranchesOptions"
                  :series="chartTopBranchesSeries"
                  height="380"
                />
              </div>
              <div v-else class="empty-state">
                <p>ไม่มีข้อมูล</p>
              </div>
            </div>
            <div v-else class="loading-state">
              <v-progress-linear indeterminate />
            </div>
          </v-card-text>
        </v-card>

        <!-- Pie Chart: Distribution by Year -->
        <v-card class="chart-card" elevation="2">
          <v-card-text class="pa-6">
            <div class="chart-header">
              <h3 class="chart-title">📈 การกระจายตามปีการศึกษา</h3>
            </div>
            <div v-if="!isLoading" class="chart-container">
              <div v-if="hasYearData" class="chart-wrapper">
                <apexchart
                  v-if="chartYearSeries.length > 0"
                  type="pie"
                  :options="chartYearOptions"
                  :series="chartYearSeries"
                  height="380"
                />
              </div>
              <div v-else class="empty-state">
                <p>ไม่มีข้อมูล</p>
              </div>
            </div>
            <div v-else class="loading-state">
              <v-progress-linear indeterminate />
            </div>
          </v-card-text>
        </v-card>
      </div>

      <!-- Charts Grid - Row 2 -->
      <div class="charts-grid mb-8">
        <!-- Column Chart: Students by Level and Year -->
        <v-card class="chart-card" elevation="2">
          <v-card-text class="pa-6">
            <div class="chart-header">
              <h3 class="chart-title">📊 นักเรียนตามระดับการศึกษา</h3>
            </div>
            <div v-if="!isLoading" class="chart-container">
              <div v-if="hasLevelData" class="chart-wrapper">
                <apexchart
                  v-if="chartLevelSeries.length > 0"
                  type="bar"
                  :options="chartLevelOptions"
                  :series="chartLevelSeries"
                  height="380"
                />
              </div>
              <div v-else class="empty-state">
                <p>ไม่มีข้อมูล</p>
              </div>
            </div>
            <div v-else class="loading-state">
              <v-progress-linear indeterminate />
            </div>
          </v-card-text>
        </v-card>

        <!-- Donut Chart: Distribution by Level -->
        <v-card class="chart-card" elevation="2">
          <v-card-text class="pa-6">
            <div class="chart-header">
              <h3 class="chart-title">🎓 สัดส่วนตามระดับการศึกษา</h3>
            </div>
            <div v-if="!isLoading" class="chart-container">
              <div v-if="hasLevelDistribution" class="chart-wrapper">
                <apexchart
                  v-if="chartLevelDistributionSeries.length > 0"
                  type="donut"
                  :options="chartLevelDistributionOptions"
                  :series="chartLevelDistributionSeries"
                  height="380"
                />
              </div>
              <div v-else class="empty-state">
                <p>ไม่มีข้อมูล</p>
              </div>
            </div>
            <div v-else class="loading-state">
              <v-progress-linear indeterminate />
            </div>
          </v-card-text>
        </v-card>
      </div>

 
      <!-- Data Table -->
      <v-card class="table-card" elevation="2">
        <v-card-text class="pa-6">
          <div class="table-header mb-4">
            <h3 class="table-title">📋 รายละเอียดข้อมูล</h3>
            <span class="table-info">
              จำนวนระเบียน: {{ filteredData.length }}
            </span>
          </div>

          <v-data-table
            v-if="filteredData.length > 0"
            :headers="tableHeaders"
            :items="filteredData"
            :items-per-page="10"
            class="data-table"
            elevation="0"
          >
            <template v-slot:item.total_students="{ item }">
              <span class="count-badge">
                {{ formatNumber(parseInt(item.total_students || 0)) }}
              </span>
            </template>
            <template v-slot:item.level_1_students="{ item }">
              <span class="level-badge level-1">
                {{ formatNumber(parseInt(item.level_1_students || 0)) }}
              </span>
            </template>
            <template v-slot:item.level_2_students="{ item }">
              <span class="level-badge level-2">
                {{ formatNumber(parseInt(item.level_2_students || 0)) }}
              </span>
            </template>
            <template v-slot:item.level_3_students="{ item }">
              <span class="level-badge level-3">
                {{ formatNumber(parseInt(item.level_3_students || 0)) }}
              </span>
            </template>
          </v-data-table>

          <div v-else class="empty-table-state">
            <p>ไม่มีข้อมูล</p>
          </div>
        </v-card-text>
      </v-card>
    </v-container>
  </div>
</template>

<script>
import * as XLSX from "xlsx";

export default {
  name: "StudentCountByBranchDashboard",
  data() {
    return {
      ApiKey: "HRvec2021",
      allData: [],
      filteredData: [],
      isLoading: false,
      exporting: false,
      filters: {
        branch_name: null,
        academic_year: null
      },
      tableHeaders: [
        { text: "ลำดับ", value: "index", width: "60px" },
        { text: "สาขาวิชา", value: "branch_name" },
        { text: "ปีการศึกษา", value: "academic_year", width: "120px" },
        { text: "ระดับ 1", value: "level_1_students", width: "100px", align: "center" },
        { text: "ระดับ 2", value: "level_2_students", width: "100px", align: "center" },
        { text: "ระดับ 3", value: "level_3_students", width: "100px", align: "center" },
        {
          text: "รวมทั้งหมด",
          value: "total_students",
          width: "120px",
          align: "right"
        },
        { text: "จำนวนวิทยาลัย", value: "college_count", width: "100px", align: "center" },
        { text: "จำนวนจังหวัด", value: "province_count", width: "100px", align: "center" }
      ]
    };
  },
  computed: {
    uniqueBranches() {
      return [...new Set(this.allData.map(item => item.branch_name))]
        .filter(b => b)
        .sort();
    },
    uniqueYears() {
      return [...new Set(this.allData.map(item => item.academic_year))]
        .filter(y => y)
        .sort((a, b) => b - a);
    },
    totalStudents() {
      return this.filteredData.reduce(
        (sum, item) => sum + parseInt(item.total_students || 0),
        0
      );
    },
    totalColleges() {
      return [...new Set(this.filteredData.map(item => item.college_count))].length;
    },
    totalProvinces() {
      return [...new Set(this.filteredData.map(item => item.province_count))].length;
    },
    groupedByBranch() {
      const grouped = {};
      this.filteredData.forEach(item => {
        const branch = item.branch_name || "ไม่ระบุ";
        if (!grouped[branch]) {
          grouped[branch] = 0;
        }
        grouped[branch] += parseInt(item.total_students || 0);
      });
      return grouped;
    },
    groupedByYear() {
      const grouped = {};
      this.filteredData.forEach(item => {
        const year = item.academic_year;
        if (!grouped[year]) {
          grouped[year] = 0;
        }
        grouped[year] += parseInt(item.total_students || 0);
      });
      return grouped;
    },
    groupedByLevel() {
      return {
        level_1: this.filteredData.reduce(
          (sum, item) => sum + parseInt(item.level_1_students || 0),
          0
        ),
        level_2: this.filteredData.reduce(
          (sum, item) => sum + parseInt(item.level_2_students || 0),
          0
        ),
        level_3: this.filteredData.reduce(
          (sum, item) => sum + parseInt(item.level_3_students || 0),
          0
        )
      };
    },
    hasTopBranchesData() {
      return Object.keys(this.groupedByBranch).length > 0;
    },
    hasYearData() {
      return Object.keys(this.groupedByYear).length > 0;
    },
    hasLevelData() {
      return this.filteredData.length > 0;
    },
    hasLevelDistribution() {
      return Object.values(this.groupedByLevel).some(v => v > 0);
    },
    hasAllBranchesData() {
      return Object.keys(this.groupedByBranch).length > 0;
    },

    // Chart: Top Branches
    chartTopBranchesSeries() {
      try {
        const branches = Object.keys(this.groupedByBranch)
          .sort((a, b) => this.groupedByBranch[b] - this.groupedByBranch[a])
          .slice(0, 15);
        const data = branches.map(b => this.groupedByBranch[b]);
        return data.length > 0
          ? [{ name: "จำนวนนักเรียน/นักศึกษา", data: data }]
          : [];
      } catch (error) {
        console.error("Error in chartTopBranchesSeries:", error);
        return [];
      }
    },
    chartTopBranchesOptions() {
      const branches = Object.keys(this.groupedByBranch)
        .sort((a, b) => this.groupedByBranch[b] - this.groupedByBranch[a])
        .slice(0, 15);
      return {
        chart: {
          type: "bar",
          fontFamily: "Roboto, sans-serif",
          toolbar: {
            show: true,
            tools: {
              download: true,
              selection: true,
              zoom: true,
              zoomin: true,
              zoomout: true,
              pan: true,
              reset: true
            }
          },
          animations: { enabled: true, speed: 800 }
        },
        colors: ["#10B981"],
        plotOptions: {
          bar: {
            horizontal: true,
            columnWidth: "70%",
            borderRadius: 4,
            dataLabels: { position: "right" }
          }
        },
        dataLabels: {
          enabled: true,
          offsetX: 10,
          style: { fontSize: "12px", colors: ["#10B981"], fontWeight: 600 },
          formatter: val => this.formatNumberShort(val)
        },
        stroke: { show: true, width: 2, colors: ["#ffffff"] },
        grid: {
          borderColor: "#e5e7eb",
          strokeDashArray: 3,
          padding: { top: 10, right: 10, bottom: 0, left: 10 }
        },
        tooltip: {
          theme: "light",
          y: { formatter: val => `${this.formatNumber(val)} คน` }
        },
        xaxis: {
          categories: branches,
          labels: { style: { fontSize: "12px" } }
        }
      };
    },

    // Chart: Year Distribution (Pie)
    chartYearSeries() {
      try {
        const years = Object.keys(this.groupedByYear).sort();
        return years.map(year => this.groupedByYear[year]);
      } catch (error) {
        console.error("Error in chartYearSeries:", error);
        return [];
      }
    },
    chartYearOptions() {
      const years = Object.keys(this.groupedByYear).sort();
      return {
        chart: {
          type: "pie",
          fontFamily: "Roboto, sans-serif",
          toolbar: {
            show: true,
            tools: { download: true, selection: true, zoom: true, zoomin: true, zoomout: true, pan: true, reset: true }
          },
          animations: { enabled: true, speed: 800 }
        },
        colors: ["#3B82F6", "#10B981", "#F59E0B", "#EF4444", "#8B5CF6", "#EC4899"],
        labels: years,
        legend: { position: "bottom", fontSize: "12px", fontFamily: "Roboto, sans-serif" },
        tooltip: {
          theme: "light",
          y: { formatter: val => `${this.formatNumber(val)} คน` }
        },
        dataLabels: {
          enabled: true,
          formatter: val => `${val.toFixed(1)}%`,
          style: { fontSize: "11px", fontWeight: 600 }
        }
      };
    },

    // Chart: By Level (Column)
    chartLevelSeries() {
      try {
        const years = Object.keys(this.groupedByYear).sort();
        return [
          {
            name: "ระดับ 1",
            data: years.map(year => {
              const yearData = this.filteredData.filter(
                d => d.academic_year == year
              );
              return yearData.reduce((sum, d) => sum + parseInt(d.level_1_students || 0), 0);
            })
          },
          {
            name: "ระดับ 2",
            data: years.map(year => {
              const yearData = this.filteredData.filter(
                d => d.academic_year == year
              );
              return yearData.reduce((sum, d) => sum + parseInt(d.level_2_students || 0), 0);
            })
          },
          {
            name: "ระดับ 3",
            data: years.map(year => {
              const yearData = this.filteredData.filter(
                d => d.academic_year == year
              );
              return yearData.reduce((sum, d) => sum + parseInt(d.level_3_students || 0), 0);
            })
          }
        ];
      } catch (error) {
        console.error("Error in chartLevelSeries:", error);
        return [];
      }
    },
    chartLevelOptions() {
      const years = Object.keys(this.groupedByYear).sort();
      return {
        chart: {
          type: "column",
          fontFamily: "Roboto, sans-serif",
          toolbar: {
            show: true,
            tools: {
              download: true,
              selection: true,
              zoom: true,
              zoomin: true,
              zoomout: true,
              pan: true,
              reset: true
            }
          },
          animations: { enabled: true, speed: 800 }
        },
        colors: ["#F59E0B", "#10B981", "#8B5CF6"],
        plotOptions: {
          bar: { horizontal: false, columnWidth: "70%", borderRadius: 4 }
        },
        dataLabels: { enabled: false },
        stroke: { show: true, width: 2, colors: ["#ffffff"] },
        grid: {
          borderColor: "#e5e7eb",
          strokeDashArray: 3,
          padding: { top: 10, right: 10, bottom: 0, left: 10 }
        },
        tooltip: {
          theme: "light",
          y: { formatter: val => `${this.formatNumber(val)} คน` }
        },
        xaxis: {
          categories: years,
          labels: { style: { fontSize: "12px" } }
        },
        yaxis: {
          title: { text: "จำนวน (คน)", style: { fontSize: "12px", fontWeight: 600 } },
          labels: { formatter: val => this.formatNumberShort(val), style: { fontSize: "11px" } }
        },
        legend: { position: "top", fontSize: "12px" }
      };
    },

    // Chart: Level Distribution (Donut)
    chartLevelDistributionSeries() {
      return [this.groupedByLevel.level_1, this.groupedByLevel.level_2, this.groupedByLevel.level_3];
    },
    chartLevelDistributionOptions() {
      return {
        chart: {
          type: "donut",
          fontFamily: "Roboto, sans-serif",
          toolbar: {
            show: true,
            tools: {
              download: true,
              selection: true,
              zoom: true,
              zoomin: true,
              zoomout: true,
              pan: true,
              reset: true
            }
          },
          animations: { enabled: true, speed: 800 }
        },
        colors: ["#F59E0B", "#10B981", "#8B5CF6"],
        plotOptions: {
          pie: {
            donut: {
              size: "65%",
              labels: {
                show: true,
                name: { fontSize: "12px" },
                value: { fontSize: "16px", fontWeight: 600, formatter: val => this.formatNumberShort(val) },
                total: {
                  show: true,
                  fontSize: "13px",
                  fontWeight: 600,
                  label: "รวม",
                  formatter: () => this.formatNumber(this.totalStudents)
                }
              }
            }
          }
        },
        labels: ["ระดับ 1", "ระดับ 2", "ระดับ 3"],
        legend: { position: "bottom", fontSize: "12px" },
        tooltip: {
          theme: "light",
          y: { formatter: val => `${this.formatNumber(val)} คน` }
        },
        dataLabels: {
          enabled: true,
          formatter: val => `${val.toFixed(1)}%`,
          style: { fontSize: "11px", fontWeight: 600 }
        }
      };
    },

    // Chart: All Branches (Horizontal)
    chartAllBranchesSeries() {
      try {
        const branches = Object.keys(this.groupedByBranch)
          .sort((a, b) => this.groupedByBranch[b] - this.groupedByBranch[a]);
        const data = branches.map(b => this.groupedByBranch[b]);
        return data.length > 0
          ? [{ name: "จำนวนนักเรียน/นักศึกษา", data: data }]
          : [];
      } catch (error) {
        console.error("Error in chartAllBranchesSeries:", error);
        return [];
      }
    },
    chartAllBranchesOptions() {
      const branches = Object.keys(this.groupedByBranch)
        .sort((a, b) => this.groupedByBranch[b] - this.groupedByBranch[a]);
      const chartHeight = Math.max(branches.length * 30, 400);
      return {
        chart: {
          type: "bar",
          fontFamily: "Roboto, sans-serif",
          toolbar: {
            show: true,
            tools: {
              download: true,
              selection: true,
              zoom: true,
              zoomin: true,
              zoomout: true,
              pan: true,
              reset: true
            }
          },
          animations: { enabled: true, speed: 800 }
        },
        colors: ["#06B6D4"],
        plotOptions: {
          bar: { horizontal: true, columnWidth: "70%", borderRadius: 4 }
        },
        dataLabels: {
          enabled: true,
          offsetX: 10,
          style: { fontSize: "12px", colors: ["#06B6D4"], fontWeight: 600 },
          formatter: val => this.formatNumberShort(val)
        },
        stroke: { show: true, width: 2, colors: ["#ffffff"] },
        grid: {
          borderColor: "#e5e7eb",
          strokeDashArray: 3,
          padding: { top: 10, right: 10, bottom: 0, left: 10 }
        },
        tooltip: {
          theme: "light",
          y: { formatter: val => `${this.formatNumber(val)} คน` }
        },
        xaxis: {
          categories: branches,
          labels: { style: { fontSize: "12px" } }
        }
      };
    }
  },
  watch: {
    // เมื่อ allData เปลี่ยน ให้ recompute filteredData
    allData: {
      handler() {
        this.applyFilters();
      },
      deep: true
    }
  },
  async mounted() {
    await this.fetchData();
  },
  methods: {
    formatNumber(num) {
      if (!num) return "0";
      return parseInt(num).toLocaleString("th-TH");
    },
    formatNumberShort(num) {
      if (!num) return "0";
      const n = parseInt(num);
      if (n >= 1000000) {
        return (n / 1000000).toFixed(1) + "M";
      } else if (n >= 1000) {
        return (n / 1000).toFixed(1) + "K";
      }
      return n.toLocaleString("th-TH");
    },
    async fetchData() {
      try {
        this.isLoading = true;
        const response = await this.$http.post(
          "vw_student_count_by_branch.php",
          { ApiKey: this.ApiKey }
        );

        if (response.data && response.data.status) {
          this.allData = response.data.data || [];
          this.applyFilters();
        } else {
          console.error("API error:", response.data);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        this.isLoading = false;
      }
    },
    applyFilters() {
      this.filteredData = this.allData
        .filter(item => {
          const branchMatch =
            !this.filters.branch_name ||
            item.branch_name === this.filters.branch_name;
          const yearMatch =
            !this.filters.academic_year ||
            item.academic_year === this.filters.academic_year;
          return branchMatch && yearMatch;
        })
        .map((item, index) => ({
          ...item,
          index: index + 1
        }));
    },
    // เพิ่มเมธอด updateBranchFilter เพื่อจัดการการเปลี่ยนแปลง filter
    updateBranchFilter(value) {
      this.filters.branch_name = value;
      this.applyFilters();
    },
    // เพิ่มเมธอด updateYearFilter เพื่อจัดการการเปลี่ยนแปลง filter
    updateYearFilter(value) {
      this.filters.academic_year = value;
      this.applyFilters();
    },
    resetFilters() {
      this.filters.branch_name = null;
      this.filters.academic_year = null;
      this.applyFilters();
    },
    async exportToExcel() {
      try {
        this.exporting = true;

        // Prepare detail data
        const exportData = this.filteredData.map(item => ({
          "ลำดับ": item.index,
          "สาขาวิชา": item.branch_name,
          "ปีการศึกษา": item.academic_year,
          "ระดับ 1": parseInt(item.level_1_students || 0),
          "ระดับ 2": parseInt(item.level_2_students || 0),
          "ระดับ 3": parseInt(item.level_3_students || 0),
          "รวมทั้งหมด": parseInt(item.total_students || 0),
          "จำนวนวิทยาลัย": parseInt(item.college_count || 0),
          "จำนวนจังหวัด": parseInt(item.province_count || 0)
        }));

        const ws = XLSX.utils.json_to_sheet(exportData);
        const wscols = [
          { wch: 8 },
          { wch: 30 },
          { wch: 15 },
          { wch: 12 },
          { wch: 12 },
          { wch: 12 },
          { wch: 15 },
          { wch: 15 },
          { wch: 15 }
        ];
        ws["!cols"] = wscols;

        // Summary sheet
        const summaryData = [
          ["สรุปข้อมูลสถิตินักเรียน/นักศึกษาตามสาขาวิชา"],
          [],
          ["รายการ", "ค่า"],
          ["รวมนักเรียน/นักศึกษา", this.totalStudents],
          ["ระดับ 1", this.groupedByLevel.level_1],
          ["ระดับ 2", this.groupedByLevel.level_2],
          ["ระดับ 3", this.groupedByLevel.level_3],
          [],
          ["จำนวนสาขาวิชา", this.uniqueBranches.length],
          ["จำนวนปีการศึกษา", this.uniqueYears.length],
          ["วันที่ส่งออก", new Date().toLocaleString("th-TH")]
        ];

        const wsSummary = XLSX.utils.aoa_to_sheet(summaryData);
        wsSummary["!cols"] = [{ wch: 30 }, { wch: 20 }];

        // Branch statistics sheet
        const branchStats = [["สาขาวิชา", "จำนวนนักเรียน", "ร้อยละ"]];
        const branches = Object.keys(this.groupedByBranch)
          .sort((a, b) => this.groupedByBranch[b] - this.groupedByBranch[a]);
        branches.forEach(branch => {
          const count = this.groupedByBranch[branch];
          const percentage = ((count / this.totalStudents) * 100).toFixed(2);
          branchStats.push([branch, count, percentage]);
        });

        const wsBranchStats = XLSX.utils.aoa_to_sheet(branchStats);
        wsBranchStats["!cols"] = [{ wch: 30 }, { wch: 15 }, { wch: 15 }];

        // Create workbook
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, wsSummary, "สรุป");
        XLSX.utils.book_append_sheet(wb, wsBranchStats, "สถิติสาขาวิชา");
        XLSX.utils.book_append_sheet(wb, ws, "รายละเอียด");

        const now = new Date();
        const dateStr = now.toISOString().slice(0, 10);
        const filename = `สถิตินักเรียนตามสาขาวิชา_${dateStr}.xlsx`;

        XLSX.writeFile(wb, filename);

        this.$notify({
          group: "foo",
          title: "สำเร็จ",
          text: "ส่งออกข้อมูล Excel สำเร็จ!",
          type: "success"
        });
      } catch (error) {
        console.error("Error exporting to Excel:", error);
        this.$notify({
          group: "foo",
          title: "ข้อผิดพลาด",
          text: "เกิดข้อผิดพลาดในการส่งออกข้อมูล",
          type: "error"
        });
      } finally {
        this.exporting = false;
      }
    }
  }
};
</script>

<style scoped>
.branch-dashboard {
  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
  min-height: 100vh;
  font-family: "Roboto", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
}

/* Header Section */
.dashboard-header {
  background: white;
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
}

.header-content {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 24px;
}

.header-title-section {
  flex: 1;
}

.dashboard-title {
  font-size: 32px;
  font-weight: 700;
  color: #1f2937;
  margin-bottom: 8px;
  letter-spacing: -0.5px;
}

.dashboard-subtitle {
  font-size: 15px;
  color: #6b7280;
  font-weight: 500;
  margin: 0;
}

.header-actions {
  display: flex;
  gap: 12px;
}

/* Filter Card */
.filter-card {
  background: white;
  border-radius: 12px;
}

.filter-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 20px;
  align-items: flex-end;
}

.filter-item {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.filter-label {
  font-size: 12px;
  font-weight: 700;
  color: #374151;
  text-transform: uppercase;
  letter-spacing: 0.8px;
}

.filter-input {
  border-radius: 6px;
}

.filter-actions {
  display: flex;
  gap: 12px;
  justify-content: flex-end;
}

.btn-reset {
  border-radius: 6px;
  font-weight: 600;
  text-transform: none;
  letter-spacing: 0;
}

/* Statistics Cards */
.stats-row {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 20px;
}

.stat-card {
  background: white;
  border-radius: 12px;
  display: flex;
  align-items: center;
  gap: 16px;
  transition: all 0.3s ease;
  border-left: 4px solid;
}

.stat-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 12px 24px rgba(0, 0, 0, 0.15) !important;
}

.stat-card-total {
  border-left-color: #3b82f6;
}

.stat-card-branch {
  border-left-color: #10b981;
}

.stat-card-college {
  border-left-color: #f59e0b;
}

.stat-card-province {
  border-left-color: #8b5cf6;
}

.stat-icon {
  font-size: 40px;
  min-width: 70px;
  text-align: center;
}

.stat-info {
  flex: 1;
  padding: 8px 0;
}

.stat-label {
  font-size: 12px;
  color: #6b7280;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 6px;
}

.stat-value {
  font-size: 28px;
  font-weight: 700;
  color: #1f2937;
}

/* Charts Grid */
.charts-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(500px, 1fr));
  gap: 24px;
}

.chart-card {
  background: white;
  border-radius: 12px;
  transition: all 0.3s ease;
}

.chart-card:hover {
  box-shadow: 0 12px 24px rgba(0, 0, 0, 0.15) !important;
}

.chart-header {
  border-bottom: 2px solid #e5e7eb;
  padding-bottom: 16px;
  margin-bottom: 20px;
}

.chart-title {
  font-size: 16px;
  font-weight: 700;
  color: #1f2937;
  margin: 0;
}

.chart-container {
  min-height: 380px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.chart-wrapper {
  width: 100%;
}

.empty-state {
  width: 100%;
  padding: 40px 20px;
  text-align: center;
  color: #9ca3af;
  font-size: 14px;
  background: #f9fafb;
  border-radius: 6px;
}

.loading-state {
  width: 100%;
  min-height: 380px;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* Table Card */
.table-card {
  background: white;
  border-radius: 12px;
  transition: all 0.3s ease;
}

.table-card:hover {
  box-shadow: 0 12px 24px rgba(0, 0, 0, 0.15) !important;
}

.table-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 2px solid #e5e7eb;
  padding-bottom: 16px;
}

.table-title {
  font-size: 16px;
  font-weight: 700;
  color: #1f2937;
  margin: 0;
}

.table-info {
  font-size: 13px;
  color: #6b7280;
  font-weight: 500;
}

.data-table {
  border-radius: 6px;
}

.data-table ::v-deep .v-data-table__wrapper {
  border-radius: 6px;
}

.data-table ::v-deep thead th {
  background: linear-gradient(180deg, #f3f4f6 0%, #e5e7eb 100%) !important;
  font-weight: 700 !important;
  color: #374151 !important;
  font-size: 13px !important;
}

.data-table ::v-deep tbody tr:hover {
  background: #f9fafb !important;
}

.data-table ::v-deep tbody td {
  font-size: 13px;
  color: #1f2937;
}

.count-badge {
  background: #dbeafe;
  color: #1e40af;
  padding: 4px 12px;
  border-radius: 4px;
  font-weight: 600;
  display: inline-block;
}

.level-badge {
  padding: 4px 12px;
  border-radius: 4px;
  font-weight: 600;
  display: inline-block;
}

.level-1 {
  background: #fef3c7;
  color: #92400e;
}

.level-2 {
  background: #dcfce7;
  color: #166534;
}

.level-3 {
  background: #e9d5ff;
  color: #6b21a8;
}

.empty-table-state {
  width: 100%;
  padding: 40px 20px;
  text-align: center;
  color: #9ca3af;
  font-size: 14px;
  background: #f9fafb;
  border-radius: 6px;
}

/* Responsive */
@media (max-width: 1200px) {
  .charts-grid {
    grid-template-columns: 1fr;
  }

  .header-content {
    flex-direction: column;
    gap: 16px;
  }

  .header-actions {
    width: 100%;
    justify-content: flex-start;
  }
}

@media (max-width: 768px) {
  .dashboard-title {
    font-size: 24px;
  }

  .filter-grid {
    grid-template-columns: 1fr;
  }

  .filter-actions {
    flex-direction: column;
    width: 100%;
  }

  .btn-reset {
    width: 100%;
  }

  .stats-row {
    grid-template-columns: repeat(2, 1fr);
  }

  .stat-card {
    flex-direction: column;
    text-align: center;
    gap: 12px;
  }

  .stat-icon {
    font-size: 32px;
  }

  .stat-value {
    font-size: 24px;
  }

  .charts-grid {
    grid-template-columns: 1fr;
  }

  .chart-container {
    min-height: 320px;
  }
}

@media (max-width: 480px) {
  .dashboard-title {
    font-size: 20px;
  }

  .stats-row {
    grid-template-columns: 1fr;
  }

  .stat-card {
    flex-direction: row;
    text-align: left;
  }

  .stat-icon {
    font-size: 28px;
    min-width: 60px;
  }

  .stat-value {
    font-size: 20px;
  }

  .data-table ::v-deep {
    font-size: 12px;
  }

  .data-table ::v-deep thead th,
  .data-table ::v-deep tbody td {
    padding: 8px !important;
  }
}
</style>